import qs from "qs"
import { z } from "zod"
import { useCallback, useEffect, useReducer, useRef, useState, Fragment } from "react"
import { LoaderCircleIcon, PencilIcon, PlusIcon, TrashIcon, SettingsIcon, EyeOffIcon } from "lucide-react"
import { PopoverClose } from "@radix-ui/react-popover"
import {
  Outlet,
  useFetcher,
  useLoaderData,
  useRouteLoaderData,
  useNavigate,
  type ActionFunctionArgs,
} from "react-router"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"
import { hasPermission } from "@/machines/Authmachine"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"

import type { RootLoader } from "@/routes/root"
import type { BrandLens } from "@/utils/types"

const routeId = "brand-lenses"
const sessionKey = "brand-lenses-filter-storage"
const MAX_ORDER = 255

const createLensSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be 100 characters or less"),
  code: z.string().trim().min(1, "Code is required"),
  order: z.preprocess(
    (v) => (v === "" || v == null ? NaN : v),
    z.coerce
      .number({ error: "Order must be a number" })
      .int("Order must be a whole number")
      .min(0, "Order must be 0 or greater")
      .max(MAX_ORDER, `Order must be ${MAX_ORDER} or less`),
  ),
})

const editLensSchema = z.object({
  newCode: z.string().trim().min(1, "Code is required"),
  newOrder: z.preprocess(
    (v) => (v === "" || v == null ? NaN : v),
    z.coerce
      .number({ error: "Order must be a number" })
      .int("Order must be a whole number")
      .min(0, "Order must be 0 or greater")
      .max(MAX_ORDER, `Order must be ${MAX_ORDER} or less`),
  ),
})

type BrandLensActionReturn = {
  action: string
  errorMessage?: string
}

async function loader() {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  const projects = getProjectsByOrganisation(loadedOrganisation)

  const loadedProject =
    filters.parsed?.project == null ? projects[0] : (projects.find((p) => p === filters.parsed?.project) ?? projects[0])

  if (loadedProject.length === 0) {
    return { loadedOrganisation, projects, loadedProject: "", lenses: [] }
  }

  const lenses = await fetchAPI<BrandLens[]>(`/api/unityviewer/lens/${loadedOrganisation}/${loadedProject}`)
    .then(handleResponse)
    .then((lenses) => {
      const sorted = lenses.sort((a, b) => a.order - b.order)

      const firstActiveLens = lenses.find((lens) => lens.active)
      if (firstActiveLens != null) firstActiveLens.isDefault = true

      return sorted
    })

  return { loadedOrganisation, projects, loadedProject, lenses }
}

async function action({ request }: ActionFunctionArgs): Promise<BrandLensActionReturn> {
  const formData = await request.formData()
  const { action: formAction, organisation, project, name } = Object.fromEntries(formData) as Record<string, string>

  let errorMessage: string | undefined
  const actionType = formAction ?? "UNKNOWN"

  if (organisation.length === 0 || project.length === 0) {
    errorMessage = "Missing organisation or project"
    return { action: actionType, errorMessage }
  }

  switch (actionType) {
    case "CREATE": {
      const result = createLensSchema.safeParse({
        name: formData.get("name"),
        code: formData.get("code"),
        order: formData.get("order"),
      })

      if (!result.success) {
        errorMessage = result.error.issues[0]?.message ?? "Invalid input"
        break
      }

      const { name, code, order } = result.data

      try {
        await fetchAPI(`/api/unityviewer/lens/${organisation}/${project}`, {
          method: "POST",
          body: { name: name.trim(), code: code.trim().toUpperCase(), order },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        if (error instanceof Response && error.status === 304) {
          errorMessage = "A lens with this name already exists"
        } else {
          errorMessage = error instanceof Error ? error.message : "Error creating lens"
        }
      }
      break
    }

    case "EDIT": {
      const result = editLensSchema.safeParse({
        newCode: formData.get("newCode"),
        newOrder: formData.get("newOrder"),
      })

      if (!result.success) {
        errorMessage = result.error.issues[0]?.message ?? "Invalid input"
        break
      }

      const { newCode, newOrder } = result.data

      try {
        await fetchAPI(`/api/unityviewer/lens/${organisation}/${project}/${name}`, {
          method: "PUT",
          body: { code: newCode.trim().toUpperCase(), order: newOrder },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        if (error instanceof Response && error.status === 304) {
          errorMessage = "A lens with this name already exists"
        } else {
          errorMessage = error instanceof Error ? error.message : "Error editing lens"
        }
      }
      break
    }

    case "DELETE": {
      if (name === "") {
        errorMessage = "Missing lens name"
        break
      }

      try {
        await fetchAPI(`/api/unityviewer/lens/${organisation}/${project}/${name}`, {
          method: "DELETE",
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = error instanceof Error ? error.message : "Error deleting lens"
      }
      break
    }

    case "TOGGLE_ACTIVE": {
      const active = formData.get("active") === "true"

      if (name === "") {
        errorMessage = "Missing lens name"
        break
      }

      try {
        await fetchAPI(`/api/unityviewer/lens/${organisation}/${project}/${name}`, {
          method: "PATCH",
          body: { active },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = error instanceof Error ? error.message : "Error toggling lens status"
      }
      break
    }

    default:
      errorMessage = "Unknown action submitted."
  }

  return { action: actionType, errorMessage }
}

export function BrandLenses() {
  return <Outlet />
}

export function BrandLensesList() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useLoaderData<typeof loader>()

  const onBeforeSubmit = useCallback<OnBeforeSubmit>((formData, reason) => {
    if (reason.type !== "field") return
    if (reason.name === "organisation") formData.delete("project")
  }, [])

  if (rootData == null || data == null) return null

  const FilterControls = (
    <div className="mb-6 flex items-center gap-4">
      <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
        {rootData.organisations.length > 1 && (
          <FiltersWrapper.Select
            name="organisation"
            label="Organisation"
            defaultValue={data.loadedOrganisation}
            items={rootData.organisations}
          />
        )}
        {data.projects.length > 1 && (
          <FiltersWrapper.Select
            name="project"
            label="Project"
            defaultValue={data.loadedProject}
            items={data.projects}
          />
        )}
      </FiltersWrapper>
      <div className="flex-1" />
      <LensAddNew />
    </div>
  )

  if (data.lenses.length === 0) {
    return (
      <Fragment>
        {FilterControls}
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-gray-500">No lenses found for the selected filters</p>
        </div>
      </Fragment>
    )
  }

  return (
    <Fragment>
      {FilterControls}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead className="w-[80px]">Order</TableHead>
              <TableHead className="w-[120px]">Status</TableHead>
              <TableHead className="w-[180px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.lenses.map((lens, index) => (
              <TableRow key={lens.name} className="text-sm">
                <TableCell>{index + 1}.</TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <span>{lens.name}</span>
                    {(lens.isDefault ?? false) && (
                      <Badge variant="secondary" className="text-xs">
                        Default
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>{lens.code}</TableCell>
                <TableCell>{lens.order}</TableCell>
                <TableCell>
                  <Badge variant={lens.active ? "default" : "secondary"}>{lens.active ? "Active" : "Inactive"}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <LensEdit {...lens} />
                    <LensConfig {...lens} />
                    <LensToggle {...lens} />
                    <LensDelete {...lens} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Fragment>
  )
}

type AddLensState = {
  open: boolean
  createAnother: boolean
  validationError: string | null
  serverError: string | null
  formKey: number
}

type AddLensAction =
  | { type: "OPEN_CHANGE"; open: boolean }
  | { type: "TOGGLE_CREATE_ANOTHER"; value: boolean }
  | { type: "VALIDATION_FAILED"; message: string }
  | { type: "SUBMIT" }
  | { type: "CREATE_SUCCEEDED" }
  | { type: "CREATE_FAILED"; message: string }

const initialAddLensState: AddLensState = {
  open: false,
  createAnother: false,
  validationError: null,
  serverError: null,
  formKey: 0,
}

function addLensReducer(state: AddLensState, action: AddLensAction): AddLensState {
  switch (action.type) {
    case "OPEN_CHANGE":
      return action.open ? { ...state, open: true } : { ...initialAddLensState, formKey: state.formKey + 1 }

    case "TOGGLE_CREATE_ANOTHER":
      return { ...state, createAnother: action.value }

    case "VALIDATION_FAILED":
      return { ...state, validationError: action.message }

    case "SUBMIT":
      return { ...state, validationError: null, serverError: null }

    case "CREATE_SUCCEEDED":
      return state.createAnother
        ? { ...state, serverError: null, validationError: null, formKey: state.formKey + 1 }
        : { ...initialAddLensState, formKey: state.formKey + 1 }

    case "CREATE_FAILED":
      return { ...state, serverError: action.message }

    default:
      return state
  }
}

function LensAddNew() {
  const fetcher = useFetcher<typeof action>()
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [state, dispatch] = useReducer(addLensReducer, initialAddLensState)
  const handledResultRef = useRef<typeof fetcher.data>(undefined)

  useEffect(() => {
    if (fetcher.data == null) return
    if (fetcher.data === handledResultRef.current) return
    if (fetcher.data.action !== "CREATE") return

    handledResultRef.current = fetcher.data

    if (fetcher.data.errorMessage != null) {
      dispatch({ type: "CREATE_FAILED", message: fetcher.data.errorMessage })
    } else {
      dispatch({ type: "CREATE_SUCCEEDED" })
    }
  }, [fetcher.data])

  if (data == null || !hasPermission("writeAccess")) return null

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget)
    const result = createLensSchema.safeParse({
      name: formData.get("name"),
      code: formData.get("code"),
      order: formData.get("order"),
    })

    if (!result.success) {
      e.preventDefault()
      dispatch({ type: "VALIDATION_FAILED", message: result.error.issues[0]?.message ?? "Invalid input" })
      return
    }

    dispatch({ type: "SUBMIT" })
  }

  return (
    <Dialog open={state.open} onOpenChange={(nextOpen) => dispatch({ type: "OPEN_CHANGE", open: nextOpen })}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon className="mr-2 h-4 w-4" /> Add Lens
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Brand Lens</DialogTitle>
        </DialogHeader>
        <fetcher.Form key={state.formKey} method="POST" className="space-y-4" onSubmit={handleSubmit}>
          <input type="hidden" name="action" value="CREATE" />
          <input type="hidden" name="organisation" value={data.loadedOrganisation} />
          <input type="hidden" name="project" value={data.loadedProject} />

          <div>
            <label htmlFor="lens-name" className="text-sm font-medium">
              Name
            </label>
            <Input id="lens-name" required name="name" placeholder="Enter lens name" maxLength={100} />
            <p className="mt-1 text-xs text-gray-500">Maximum 100 characters</p>
          </div>

          <div>
            <label htmlFor="lens-code" className="text-sm font-medium">
              Code
            </label>
            <Input id="lens-code" required name="code" placeholder="Enter code" />
            <p className="mt-1 text-xs text-gray-500">Usually 3 characters, will be converted to uppercase</p>
          </div>

          <div>
            <label htmlFor="lens-order" className="text-sm font-medium">
              Order
            </label>
            <Input
              id="lens-order"
              required
              type="number"
              name="order"
              placeholder="Enter display order"
              min="0"
              max={MAX_ORDER}
            />
            <p className="mt-1 text-xs text-gray-500">Lowest value becomes default lens (0-{MAX_ORDER})</p>
          </div>

          <div className="flex items-center space-x-2 border-t pt-2">
            <Checkbox
              id="create-another"
              checked={state.createAnother}
              onCheckedChange={(checked) => dispatch({ type: "TOGGLE_CREATE_ANOTHER", value: checked === true })}
            />
            <label htmlFor="create-another" className="text-sm leading-none font-medium">
              Create another?
            </label>
          </div>

          <div className="flex items-center justify-start gap-4">
            {fetcher.state !== "idle" && (
              <span className="flex items-center gap-1">
                <LoaderCircleIcon className="size-4 animate-spin" /> Loading
              </span>
            )}
            {(state.validationError ?? state.serverError) != null && (
              <p className="text-sm text-destructive">{state.validationError ?? state.serverError}</p>
            )}
            <div className="flex-1" />
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit">Create Lens</Button>
          </div>
        </fetcher.Form>
      </DialogContent>
    </Dialog>
  )
}

function LensEdit(lens: BrandLens) {
  const [open, setOpen] = useState(false)
  const closeDialog = useCallback(() => setOpen(false), [])

  if (!hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="outline" size="icon" aria-label="Edit lens">
              <PencilIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Edit Lens</TooltipContent>
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Lens</DialogTitle>
        </DialogHeader>
        <LensEditForm {...lens} onSuccess={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function LensEditForm({ name, code, order, onSuccess }: BrandLens & { onSuccess: () => void }) {
  const fetcher = useFetcher<typeof action>()
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    if (fetcher.data?.action === "EDIT" && fetcher.data?.errorMessage == null) {
      onSuccess()
    }
  }, [fetcher.data, onSuccess])

  if (data == null) return null

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget)
    const result = editLensSchema.safeParse({
      newCode: formData.get("newCode"),
      newOrder: formData.get("newOrder"),
    })

    if (!result.success) {
      e.preventDefault()
      setValidationError(result.error.issues[0]?.message ?? "Invalid input")
      return
    }

    setValidationError(null)
  }

  return (
    <fetcher.Form method="POST" className="space-y-4" onSubmit={handleSubmit}>
      <input type="hidden" name="action" value="EDIT" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="name" value={name} />

      <div>
        <label htmlFor={`edit-lens-name-${name}`} className="text-sm font-medium">
          Name (read-only)
        </label>
        <Input id={`edit-lens-name-${name}`} value={name} disabled className="bg-gray-50" />
      </div>

      <div>
        <label htmlFor={`edit-lens-code-${name}`} className="text-sm font-medium">
          Code
        </label>
        <Input id={`edit-lens-code-${name}`} required name="newCode" defaultValue={code} />
      </div>

      <div>
        <label htmlFor={`edit-lens-order-${name}`} className="text-sm font-medium">
          Order
        </label>
        <Input
          id={`edit-lens-order-${name}`}
          required
          type="number"
          name="newOrder"
          defaultValue={order}
          min="0"
          max={MAX_ORDER}
        />
        <p className="mt-1 text-xs text-gray-500">Valid range: 0-{MAX_ORDER}</p>
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" && (
          <span className="flex items-center gap-1">
            <LoaderCircleIcon className="size-4 animate-spin" /> Loading
          </span>
        )}
        {(validationError ?? fetcher.data?.errorMessage) != null && (
          <p className="text-sm text-destructive">{validationError ?? fetcher.data?.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">Update Lens</Button>
      </div>
    </fetcher.Form>
  )
}

function LensConfig(lens: BrandLens) {
  const navigate = useNavigate()

  if (!hasPermission("writeAccess")) return null

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          onClick={() => void navigate(`configure/${lens.code}`)}
          aria-label="Configure lens"
        >
          <SettingsIcon className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Configure Lens</TooltipContent>
    </Tooltip>
  )
}

function LensToggle(lens: BrandLens) {
  const fetcher = useFetcher()
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <fetcher.Form method="POST" className="contents">
      <input type="hidden" name="action" value="TOGGLE_ACTIVE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="name" value={lens.name} />
      <input type="hidden" name="active" value={(!lens.active).toString()} />

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            type="submit"
            disabled={fetcher.state !== "idle"}
            aria-label={`${lens.active ? "Deactivate" : "Activate"} lens`}
          >
            <EyeOffIcon className={`h-4 w-4 ${lens.active ? "text-orange-500" : "text-green-500"}`} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{lens.active ? "Deactivate" : "Activate"} Lens</TooltipContent>
      </Tooltip>
    </fetcher.Form>
  )
}

function LensDelete(lens: BrandLens) {
  if (!hasPermission("deleteAccess")) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Delete lens">
          <TrashIcon className="h-4 w-4 text-red-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="max-w-sm">
        <p className="text-sm">
          Are you sure you want to delete the lens &quot;<strong>{lens.name}</strong>&quot;? This action cannot be
          undone.
        </p>
        <LensDeleteForm {...lens} />
      </PopoverContent>
    </Popover>
  )
}

function LensDeleteForm(lens: BrandLens) {
  const fetcher = useFetcher<typeof action>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null) return null

  return (
    <fetcher.Form method="POST" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="name" value={lens.name} />

      {fetcher.state !== "idle" ? (
        <span className="flex items-center gap-1 text-sm text-red-500">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
        </span>
      ) : (
        fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button type="button" variant="ghost">
          Cancel
        </Button>
      </PopoverClose>
      <Button type="submit" variant="destructive">
        Confirm Delete
      </Button>
    </fetcher.Form>
  )
}

BrandLenses.loader = loader
BrandLenses.action = action
BrandLenses.routeId = routeId
BrandLenses.List = BrandLensesList
